import { SearchResultItem } from './universal-search-utils';

export const DEFAULT_RECENT_STORAGE_KEY = 'claro.search.recent';
export const recentSearchChangeEvent = (storageKey: string) => `recent-search-change:${storageKey}`;

const RECENT_SEARCH_LIMIT = 5;

const writeRecentSearches = (storageKey: string, items: SearchResultItem[]): SearchResultItem[] => {
    window.localStorage.setItem(storageKey, JSON.stringify(items));
    window.dispatchEvent(new Event(recentSearchChangeEvent(storageKey)));
    return items;
};

export const loadRecentSearches = (storageKey: string): SearchResultItem[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        if (!Array.isArray(parsedValue)) {
            return [];
        }

        return parsedValue.filter(isSearchResultItem).slice(0, RECENT_SEARCH_LIMIT);
    } catch {
        return [];
    }
};

export const saveRecentSearch = (storageKey: string, item: SearchResultItem): SearchResultItem[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    const dedupedItems = [
        item,
        ...loadRecentSearches(storageKey).filter((recentItem) => recentItem.id !== item.id),
    ].slice(0, RECENT_SEARCH_LIMIT);

    return writeRecentSearches(storageKey, dedupedItems);
};

export const removeRecentSearch = (storageKey: string, id: string): SearchResultItem[] => {
    return removeRecentSearches(storageKey, (item) => item.id === id);
};

/** Forget the entity and any search aliases that navigate to its detail page. */
export const forgetRecentSearchEntity = (guid: string): SearchResultItem[] =>
    removeRecentSearches(DEFAULT_RECENT_STORAGE_KEY, (item) => item.id === guid || item.href.endsWith(`/${guid}`));

const removeRecentSearches = (storageKey: string, matches: (item: SearchResultItem) => boolean): SearchResultItem[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    const items = loadRecentSearches(storageKey);
    const remainingItems = items.filter((item) => !matches(item));
    return remainingItems.length === items.length ? items : writeRecentSearches(storageKey, remainingItems);
};

const isSearchResultItem = (value: unknown): value is SearchResultItem => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Partial<SearchResultItem>;
    return (
        candidate.kind === 'result' &&
        typeof candidate.id === 'string' &&
        typeof candidate.type === 'string' &&
        typeof candidate.typeLabel === 'string' &&
        typeof candidate.name === 'string' &&
        typeof candidate.href === 'string' &&
        typeof candidate.initials === 'string'
    );
};
